import { useMemo } from 'react'
import {
  fetchMedReqsByPatientHash,
  useMedReqsByPatientHashQuery,
  useMedReqsByOrderHashQuery,
  type MedReqListItem
} from '@/services/med-requests'
import { useProductCategoriesQuery } from '@/services/products'
import type { ProductCategory } from '@/types/order'
import type { MedicationRequestItem } from '@/components/shared/PreviousMedicationRequestsList'
import { useQueries } from '@tanstack/react-query'

interface UseMedReqCardsParams {
  patientHash?: string
  orderHash?: string
}

export function toMedicationRequestItem(
  item: MedReqListItem,
  categoriesMap: Record<string, ProductCategory[]> | undefined
): MedicationRequestItem {
  return {
    id: item.hash,
    name: item.medicationName ?? '',
    status: item.orderItemStatus ?? '',
    date: item.createdAt ?? '',
    orderNumber: item.orderNumber ?? undefined,
    type: item.productHash ? (categoriesMap?.[item.productHash]?.map(c => c.name).join(', ') ?? undefined) : undefined,
    subscription: item.orderType ?? undefined,
    // The API field is `prescribedItemName`; the card shape uses `prescribedItem`, so it is renamed here.
    prescribedItem: item.prescribedItemName ?? undefined
  }
}

function uniqueSortedProductHashes(items: MedReqListItem[]): string[] {
  return [...new Set(items.map(i => i.productHash).filter((h): h is string => !!h))].sort()
}

/**
 * Reusable hook that fetches med requests (scoped by patient or order)
 * and enriches them with product category data for card display.
 */
export function useMedReqCards({ patientHash, orderHash }: UseMedReqCardsParams) {
  const patientQuery = useMedReqsByPatientHashQuery(patientHash)
  const orderQuery = useMedReqsByOrderHashQuery(orderHash)

  const medReqs = patientHash ? patientQuery.data : orderQuery.data
  const isLoadingMedReqs = patientHash ? patientQuery.isLoading : orderQuery.isLoading

  const productHashes = useMemo(() => (medReqs ? uniqueSortedProductHashes(medReqs) : []), [medReqs])

  const { data: categoriesMap, isLoading: isLoadingCategories } = useProductCategoriesQuery(productHashes)

  const medicationRequests: MedicationRequestItem[] = useMemo(
    () => (medReqs ? medReqs.map(item => toMedicationRequestItem(item, categoriesMap)) : []),
    [medReqs, categoriesMap]
  )

  return {
    medicationRequests,
    isLoading: isLoadingMedReqs || (productHashes.length > 0 && isLoadingCategories)
  }
}

/**
 * Multi-patient variant: fetches med requests across N patient hashes,
 * merges them (deduping by order-item hash), and applies the same
 * product-category enrichment so the card shape stays in sync.
 */
export function useMergedPatientMedReqCards(patientHashes: string[]) {
  const { isLoadingMedReqs, mergedRaw } = useQueries({
    queries: patientHashes.map(hash => ({
      queryKey: ['med-requests', 'patient', hash],
      queryFn: () => fetchMedReqsByPatientHash(hash),
      enabled: !!hash
    })),
    combine: results => {
      const seen = new Set<string>()
      const merged: MedReqListItem[] = []
      for (const r of results) {
        if (!r.data) continue
        for (const item of r.data) {
          if (seen.has(item.hash)) continue
          seen.add(item.hash)
          merged.push(item)
        }
      }
      return { isLoadingMedReqs: results.some(r => r.isLoading), mergedRaw: merged }
    }
  })

  const productHashes = useMemo(() => uniqueSortedProductHashes(mergedRaw), [mergedRaw])

  const { data: categoriesMap, isLoading: isLoadingCategories } = useProductCategoriesQuery(productHashes)

  const medicationRequests: MedicationRequestItem[] = useMemo(
    () => mergedRaw.map(item => toMedicationRequestItem(item, categoriesMap)),
    [mergedRaw, categoriesMap]
  )

  return {
    medicationRequests,
    isLoading: isLoadingMedReqs || (productHashes.length > 0 && isLoadingCategories)
  }
}
